
// @see: https://github.com/Turfjs/turf
// Funciones trasladadas aquí para reducir o peso de javascript,
// porque truf.js importa jsts.js por completo (>500K).
//

function feature (geometry, properties) {
  return {
    type: 'Feature',
    properties: properties || {},
    geometry: geometry
  }
}

function getCoord (obj) {
  if (Array.isArray(obj) &&
    typeof obj[0] === 'number' &&
    typeof obj[1] === 'number') {
    return obj
  } else if (obj) {
    if (obj.type === 'Feature' &&
      obj.geometry &&
      obj.geometry.type === 'Point' &&
      Array.isArray(obj.geometry.coordinates)) {
      return obj.geometry.coordinates
    } else if (obj.type === 'Point' &&
      Array.isArray(obj.coordinates)) {
      return obj.coordinates
    }
  }
  throw new Error('A coordinate, feature, or point geometry is required')
}

let factors = {
  miles: 3960,
  nauticalmiles: 3441.145,
  degrees: 57.2957795,
  radians: 1,
  inches: 250905600,
  yards: 6969600,
  meters: 6373000,
  metres: 6373000,
  kilometers: 6373,
  kilometres: 6373
}

function radiansToDistance (radians, units) {
  let factor = factors[units || 'kilometers']
  if (factor === undefined) {
    throw new Error('Invalid unit')
  }
  return radians * factor
}

function distance (from, to, units) {
  let degrees2radians = Math.PI / 180
  let coordinates1 = getCoord(from)
  let coordinates2 = getCoord(to)
  let dLat = degrees2radians * (coordinates2[1] - coordinates1[1])
  let dLon = degrees2radians * (coordinates2[0] - coordinates1[0])
  let lat1 = degrees2radians * coordinates1[1]
  let lat2 = degrees2radians * coordinates2[1]

  let a = Math.pow(Math.sin(dLat / 2), 2) +
    Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2)

  return radiansToDistance(
    2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)),
    units
  )
}

module.exports.lineString = (coordinates) => {
  return feature({
    type: 'LineString',
    coordinates: coordinates
  }, null)
}

function coordEach (layer, callback, excludeWrapCoord) {
  let i
  let j
  let k
  let g
  let l
  let geometry
  let stopG
  let coords
  let geometryMaybeCollection
  let wrapShrink = 0
  let isGeometryCollection
  let isFeatureCollection = layer.type === 'FeatureCollection'
  let isFeature = layer.type === 'Feature'
  let stop = isFeatureCollection ? layer.features.length : 1

  // This logic may look a little weird. The reason why it is that way
  // is because it's trying to be fast. GeoJSON supports multiple kinds
  // of objects at its root: FeatureCollection, Features, Geometries.
  // This function has the responsibility of handling all of them, and that
  // means that some of the `for` loops you see below actually just don't apply
  // to certain inputs. For instance, if you give this just a
  // Point geometry, then both loops are short-circuited and all we do
  // is gradually rename the input until it's called 'geometry'.
  //
  // This also aims to allocate as few resources as possible: just a
  // few numbers and booleans, rather than any temporary arrays as would
  // be required with the normalization approach.
  for (i = 0; i < stop; i++) {
    geometryMaybeCollection = (isFeatureCollection ? layer.features[i].geometry : (isFeature ? layer.geometry : layer))
    isGeometryCollection = geometryMaybeCollection.type === 'GeometryCollection'
    stopG = isGeometryCollection ? geometryMaybeCollection.geometries.length : 1

    for (g = 0; g < stopG; g++) {
      geometry = isGeometryCollection ? geometryMaybeCollection.geometries[g] : geometryMaybeCollection
      coords = geometry.coordinates

      wrapShrink = (excludeWrapCoord && (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon')) ? 1 : 0

      if (geometry.type === 'Point') {
        callback(coords)
      } else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
        for (j = 0; j < coords.length; j++) callback(coords[j])
      } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
        for (j = 0; j < coords.length; j++) {
          for (k = 0; k < coords[ j ].length - wrapShrink; k++) {
            callback(coords[ j ][ k ])
          }
        }
      } else if (geometry.type === 'MultiPolygon') {
        for (j = 0; j < coords.length; j++) {
          for (k = 0; k < coords[ j ].length; k++) {
            for (l = 0; l < coords[ j ][ k ].length - wrapShrink; l++) {
              callback(coords[ j ][ k ][ l ])
            }
          }
        }
      } else {
        throw new Error('Unknown Geometry Type')
      }
    }
  }
}

module.exports.bbox = (geojson) => {
  var bbox = [Infinity, Infinity, -Infinity, -Infinity]
  coordEach(geojson, function (coord) {
    if (bbox[0] > coord[0]) bbox[0] = coord[0]
    if (bbox[1] > coord[1]) bbox[1] = coord[1]
    if (bbox[2] < coord[0]) bbox[2] = coord[0]
    if (bbox[3] < coord[1]) bbox[3] = coord[1]
  })
  return bbox
}

function point (coordinates, properties) {
  if (!Array.isArray(coordinates)) throw new Error('Coordinates must be an array')
  if (coordinates.length < 2) throw new Error('Coordinates must be at least 2 numbers long')
  return feature({
    type: 'Point',
    coordinates: coordinates.slice()
  }, properties)
}

function length (coords, units) {
  let travelled = 0
  let prevCoords = point(coords[0])
  let curCoords = point(coords[0])
  let temp
  for (let i = 1; i < coords.length; i++) {
    curCoords.geometry.coordinates = coords[i]
    travelled += distance(prevCoords, curCoords, units)
    temp = prevCoords
    prevCoords = curCoords
    curCoords = temp
  }
  return travelled
}

module.exports.lineDistance = (line, units) => {
  if (!line) {
    return 0
  }
  if (line.type === 'FeatureCollection') {
    return line.features.reduce(function (memo, feature) {
      return memo + module.exports.lineDistance(feature, units)
    }, 0)
  }

  let geometry = line.type === 'Feature' ? line.geometry : line
  let d
  let i

  if (geometry.type === 'LineString') {
    return length(geometry.coordinates, units)
  } else if (geometry.type === 'Polygon' || geometry.type === 'MultiLineString') {
    d = 0
    for (i = 0; i < geometry.coordinates.length; i++) {
      d += length(geometry.coordinates[i], units)
    }
    return d
  } else if (line.type === 'MultiPolygon') {
    d = 0
    for (i = 0; i < geometry.coordinates.length; i++) {
      for (let j = 0; j < geometry.coordinates[i].length; j++) {
        d += length(geometry.coordinates[i][j], units)
      }
    }
    return d
  } else {
    throw new Error('input must be a LineString, MultiLineString, ' +
      'Polygon, or MultiPolygon Feature or Geometry (or a FeatureCollection ' +
      'containing only those types)')
  }
}

function bearing (start, end) {
  let degrees2radians = Math.PI / 180
  let radians2degrees = 180 / Math.PI
  let coordinates1 = getCoord(start)
  let coordinates2 = getCoord(end)

  let lon1 = degrees2radians * coordinates1[0]
  let lon2 = degrees2radians * coordinates2[0]
  let lat1 = degrees2radians * coordinates1[1]
  let lat2 = degrees2radians * coordinates2[1]
  let a = Math.sin(lon2 - lon1) * Math.cos(lat2)
  let b = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)

  return radians2degrees * Math.atan2(a, b)
}

function distanceToRadians (distance, units) {
  let factor = factors[units || 'kilometers']
  if (factor === undefined) {
    throw new Error('Invalid unit')
  }
  return distance / factor
}

function destination (from, distance, bearing, units) {
  let degrees2radians = Math.PI / 180
  let radians2degrees = 180 / Math.PI
  let coordinates1 = getCoord(from)
  let longitude1 = degrees2radians * coordinates1[0]
  let latitude1 = degrees2radians * coordinates1[1]
  let bearingRad = degrees2radians * bearing

  let radians = distanceToRadians(distance, units)

  let latitude2 = Math.asin(
    Math.sin(latitude1) * Math.cos(radians) +
    Math.cos(latitude1) * Math.sin(radians) * Math.cos(bearingRad)
  )
  let longitude2 = longitude1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(radians) * Math.cos(latitude1),
    Math.cos(radians) - Math.sin(latitude1) * Math.sin(latitude2)
  )

  return point([radians2degrees * longitude2, radians2degrees * latitude2])
}

module.exports.along = (line, distanceValue, units) => {
  try {
    let coords
    if (line.type === 'Feature') {
      coords = line.geometry.coordinates
    } else if (line.type === 'LineString') {
      coords = line.coordinates
    } else {
      throw new Error('input must be a LineString Feature or Geometry')
    }

    let travelled = 0
    for (let i = 0; i < coords.length; i++) {
      if (distanceValue >= travelled && i === coords.length - 1) {
        break
      } else if (travelled >= distanceValue) {
        let overshot = distanceValue - travelled
        if (!overshot) {
          return point(coords[i])
        } else {
          let direction = bearing(coords[i], coords[i - 1]) - 180
          return destination(coords[i], overshot, direction, units) // interpolated
        }
      } else {
        travelled += distance(coords[i], coords[i + 1], units)
      }
    }
    return point(coords[coords.length - 1])
  } catch (e) {
    console.trace(e)
    return null
  }
}



// WEBPACK FOOTER //
// ./src/utils/map.js